// client/src/hooks/useCatalogoModelo.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

const CATALOGO_MODELO_QUERY_KEY = 'catalogoModelo';

export const useCatalogoModeloItems = (filters = {}) => {
    return useQuery({
        queryKey: [CATALOGO_MODELO_QUERY_KEY, filters],
        queryFn: async () => {
            const { data } = await apiClient.get('/catalogo-modelo', { params: filters });
            return {
                itens: data.data || [],
                total: data.total || 0,
                count: data.count || 0
            };
        }
    });
};

export const useMutateCatalogoModelo = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidate = () => queryClient.invalidateQueries({ queryKey: [CATALOGO_MODELO_QUERY_KEY] });

    const createItem = useMutation({
        mutationFn: (payload) => apiClient.post('/catalogo-modelo', payload),
        onSuccess: () => {
            invalidate();
            setAlert('Item de catálogo criado com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao criar item de catálogo', 'danger')
    });

    const updateItem = useMutation({
        mutationFn: ({ id, payload }) => apiClient.put(`/catalogo-modelo/${id}`, payload),
        onSuccess: () => {
            invalidate();
            setAlert('Item de catálogo atualizado com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao atualizar item de catálogo', 'danger')
    });

    const deleteItem = useMutation({
        mutationFn: (id) => apiClient.delete(`/catalogo-modelo/${id}`),
        onSuccess: () => {
            invalidate();
            setAlert('Item de catálogo removido com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao remover item de catálogo', 'danger')
    });

    return { createItem, updateItem, deleteItem };
};

export const useImportarCsvCatalogoModelo = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    return useMutation({
        mutationFn: (formData) => apiClient.post('/catalogo-modelo/importar-csv', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(r => r.data),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [CATALOGO_MODELO_QUERY_KEY] });
            const resumo = data?.data;
            if (resumo) {
                setAlert(
                    `Importação concluída: ${resumo.totalCriados} criados, ${resumo.totalDuplicados} duplicados, ${resumo.totalErros} com erro.`,
                    resumo.totalErros > 0 ? 'warning' : 'success'
                );
            }
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao importar CSV', 'danger')
    });
};

export const useImportarParaConta = () => {
    const { setAlert } = useAlert();

    return useMutation({
        mutationFn: (id) => apiClient.post(`/catalogo-modelo/${id}/importar`).then(r => r.data),
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao importar item do catálogo', 'danger')
    });
};

// Gera a proposta de catálogo por IA (não persiste nada) — timeout maior
// que o default global, mesmo raciocínio de useAIGeracao.js (o texto
// pedido aqui é grande: N categorias x quantidadePorCategoria itens).
export const useGerarCatalogoIA = () => {
    const { setAlert } = useAlert();

    return useMutation({
        mutationFn: (payload) => apiClient.post('/catalogo-modelo/gerar-ia', payload, { timeout: 150000 }).then(r => r.data.data),
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao gerar catálogo com IA.', 'danger')
    });
};

export const useCriarEmMassaCatalogoModelo = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    return useMutation({
        mutationFn: (payload) => apiClient.post('/catalogo-modelo/criar-em-massa', payload).then(r => r.data),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [CATALOGO_MODELO_QUERY_KEY] });
            const resumo = data?.data;
            if (resumo) {
                setAlert(
                    `Catálogo criado: ${resumo.totalCriados} itens criados, ${resumo.totalDuplicados} duplicados, ${resumo.totalErros} com erro.`,
                    resumo.totalErros > 0 ? 'warning' : 'success'
                );
            }
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao criar itens do catálogo', 'danger')
    });
};

export const useImportarCategoriaCatalogo = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    return useMutation({
        mutationFn: (payload) => apiClient.post('/catalogo-modelo/importar-categoria', payload).then(r => r.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['servicos'] });
            queryClient.invalidateQueries({ queryKey: ['itensVenda'] });
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao importar categoria do catálogo', 'danger')
    });
};
